import { useCallback } from 'react';
import { SnowflakeReportUpdateActions } from 'reporting-snowflake/relational/updaters/report/use-snowflake-updater';
import { useRelationalReportInfrastructure } from '../../contexts/RelationalReportContext';
/**
 * Generic function to execute any snowflake report updater action
 * Pure function that takes all required parameters
 */
const executeSnowflakeOperation = (snowflakeUpdater, action, parameters, originalReport) => {
  try {
    const updatedReportDefinition = snowflakeUpdater(action, parameters);
    if (updatedReportDefinition.error) {
      return {
        error: updatedReportDefinition.error,
        updatedReport: null
      };
    }
    const updatedReport = originalReport.set('reportDefinition', updatedReportDefinition.report.toMap());
    return {
      error: null,
      updatedReport
    };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      updatedReport: null
    };
  }
};
const useSRVSnowflakeUpdater = report => {
  const {
    snowflakeUpdater
  } = useRelationalReportInfrastructure();
  const addColumnToChannel = useCallback((column, channelName) => {
    return executeSnowflakeOperation(snowflakeUpdater, SnowflakeReportUpdateActions.ADD_COLUMN_TO_CHANNEL, {
      column,
      channelName
    }, report);
  }, [snowflakeUpdater, report]);
  const updateVisualType = useCallback(visualType => {
    return executeSnowflakeOperation(snowflakeUpdater, SnowflakeReportUpdateActions.UPDATE_VISUAL_TYPE, {
      type: visualType
    }, report);
  }, [snowflakeUpdater, report]);
  const moveColumnToStagedColumns = useCallback((originalColumn, newColumnForStagedColumns) => {
    return executeSnowflakeOperation(snowflakeUpdater, SnowflakeReportUpdateActions.MOVE_COLUMN_TO_STAGED_COLUMNS, {
      originalColumn,
      newColumnForStagedColumns
    }, report);
  }, [snowflakeUpdater, report]);
  const removeColumnFromReportColumns = useCallback(column => {
    return executeSnowflakeOperation(snowflakeUpdater, SnowflakeReportUpdateActions.REMOVE_COLUMN_FROM_REPORT_COLUMNS, {
      column
    }, report);
  }, [snowflakeUpdater, report]);
  const updateEncodedColumnWithExpressionAddStaged = useCallback((alias, newColumn, originalColumn, isReplace, expressionField) => {
    return executeSnowflakeOperation(snowflakeUpdater, SnowflakeReportUpdateActions.UPDATE_ENCODED_COLUMN_WITH_EXPRESSION_ADD_STAGED, {
      alias,
      newColumn,
      originalColumn,
      isReplace,
      expressionField
    }, report);
  }, [snowflakeUpdater, report]);
  const updateEncodedColumnWithExpression = useCallback((alias, column, isReplace, expressionField) => {
    return executeSnowflakeOperation(snowflakeUpdater, SnowflakeReportUpdateActions.UPDATE_ENCODED_COLUMN_WITH_EXPRESSION, {
      alias,
      column,
      isReplace,
      expressionField
    }, report);
  }, [snowflakeUpdater, report]);
  const updateColumnSort = useCallback((columnAlias, column) => {
    return executeSnowflakeOperation(snowflakeUpdater, SnowflakeReportUpdateActions.UPDATE_ENCODED_COLUMN, {
      alias: columnAlias,
      column,
      isReplace: false
    }, report);
  }, [snowflakeUpdater, report]);
  return {
    addColumnToChannel,
    updateVisualType,
    moveColumnToStagedColumns,
    removeColumnFromReportColumns,
    updateEncodedColumnWithExpressionAddStaged,
    updateEncodedColumnWithExpression,
    updateColumnSort
  };
};
export default useSRVSnowflakeUpdater;